// Reseñas de Google (PRD H.1–H.4): botón con aviso de cupo de SerpApi, pestaña de la ficha
// y apertura basada en reseñas para Match y Guion. Las cuentas las hace la BD
// (review_summary_refresh, review_brief); aquí solo se muestran.
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotify } from "ra-core";
import { Copy, MapPin, MessageCircle, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { waHref } from "../contactLinks";
import { withOptOut } from "../templates/fillTemplate";
import { OfferDot } from "../ui";
import { offerColor, useOffers } from "../useOffers";
import { box, Quote } from "../ficha/FichaTabs";
import type { Ficha } from "../ficha/useFicha";
import { useIsDueno } from "../useIsDueno";

const sb = () => getSupabaseClient();

export type ReviewTheme = {
  key: string;
  label: string;
  offer_slugs: string[];
  solutions: Record<string, string>;
  praise: string | null;
};
type ThemeStat = {
  k: string;
  m: number;
  neg: number;
  tr: number;
  pain: boolean;
};
export type ReviewSummary = {
  directory_id: number;
  rating: number | null;
  total: number | null;
  analyzed: number | null;
  own_total: number | null;
  owner_reply_rate: number | null;
  dist: number[] | null;
  by_quarter: { q: string; rating: number; n: number }[] | null;
  themes: ThemeStat[] | null;
  includes_competitors: boolean;
  competitors:
    | { name: string; rating: number; total: number; pain: string | null }[]
    | null;
  status:
    | "en_cola"
    | "descargando"
    | "analizando"
    | "lista"
    | "error"
    | "vinculada";
  job: string | null;
  error: string | null;
  searches: number;
  fetched_at: string | null;
  max_reviews: number;
  data_id: string | null;
  candidate: (Place & { check: PlaceCheck }) | null;
  not_found_at: string | null;
  maps_url: string | null;
  place_check:
    | (Partial<PlaceCheck> & { por?: string; antes_de_verificar?: boolean })
    | null;
};
export type Place = {
  data_id: string;
  name: string;
  address: string | null;
  total?: number | null;
  rating?: number | null;
};
export type PlaceCheck = {
  ok: boolean;
  outcome?: "coincide" | "dudosa" | "no_coincide";
  score?: number;
  tel: boolean;
  nombre: number;
  cp: boolean | null;
  municipio: boolean;
  distancia_m?: number | null;
};
type ReviewRow = {
  id: number;
  rating: number;
  body: string | null;
  published_at: string | null;
  owner_replied: boolean;
  owner_reply: string | null;
  themes: string[];
  neg_themes: string[];
  other_theme: string | null;
};
export type ReviewBrief = {
  calificacion: number;
  total: number;
  incluye_competidores: boolean;
  dolores: {
    tema: string;
    productos: string[];
    menciones: number;
    cita: string | null;
  }[];
  aman: string[];
} | null;

export const useReviewThemes = () =>
  useQuery({
    queryKey: ["review_themes"],
    staleTime: Infinity,
    queryFn: async () => {
      const { data } = await sb().from("review_themes").select("*");
      return new Map(((data ?? []) as ReviewTheme[]).map((t) => [t.key, t]));
    },
  });

const BUSY = ["en_cola", "descargando", "analizando"];
export const useReviewSummary = (dirId?: number | null) =>
  useQuery({
    queryKey: ["review_summary", dirId],
    enabled: !!dirId,
    refetchInterval: (q) =>
      BUSY.includes((q.state.data as ReviewSummary | null)?.status ?? "")
        ? 5000
        : false,
    queryFn: async () => {
      const { data } = await sb()
        .from("review_summary")
        .select("*")
        .eq("directory_id", dirId!)
        .maybeSingle();
      return data as ReviewSummary | null;
    },
  });

export const useReviewBrief = (dirId?: number | null) => {
  // Bloque 0b: reviews are owner-only (freelancers get no review-based opening)
  const isDueno = useIsDueno();
  return useQuery({
    queryKey: ["review_brief", dirId],
    enabled: !!dirId && isDueno,
    queryFn: async () => {
      const { data } = await sb().rpc("review_brief", { p_dir: dirId! });
      return (data ?? null) as ReviewBrief;
    },
  });
};

/**
 * Apertura basada en reseñas (reglas del prototipo): empieza por lo que aman, menciona el
 * patrón (nunca a un cliente ni "leí sus reseñas malas") y termina preguntando.
 */
export const revOpener = (
  b: ReviewBrief | undefined,
  offerName: (slug: string) => string,
  slug?: string,
) => {
  const p = b?.dolores.find((d) =>
    slug ? d.productos.includes(slug) : d.productos.length,
  );
  if (!b || !p) return null;
  const love = b.aman[0];
  return `Vi lo que comentan sus clientes en Google: ${love ? `destacan «${love.toLowerCase()}», y ` : ""}varias reseñas mencionan «${p.tema.toLowerCase()}»${b.incluye_competidores ? " (algo que se repite en su giro y zona)" : ` (${p.menciones})`}. Justo eso le resolvemos a negocios como el suyo con ${offerName(slug ?? p.productos[0])}. ¿Les pasa seguido?`;
};

const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);
const fecha = (s?: string | null) =>
  s ? s.slice(0, 10).split("-").reverse().join("/") : "—";

/* --------------------------------------------------------- botón con cupo */
export const ReviewsButton = ({
  ids,
  job = "completa",
  label = "Analizar reseñas",
  onDone,
  size = "sm",
  variant,
  askMax = true,
}: {
  ids: number[];
  job?: "completa" | "nuevas" | "competidores";
  label?: string;
  onDone?: () => void;
  size?: "sm" | "default";
  variant?: "outline";
  askMax?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [max, setMax] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const notify = useNotify();
  const qc = useQueryClient();
  const { data: est, isFetching } = useQuery({
    queryKey: ["reviews_estimate", ids, max, job],
    enabled: open,
    queryFn: async () => {
      const { data, error } = await sb().rpc("reviews_estimate", {
        p_ids: ids,
        p_max: max,
        p_job: job,
      });
      if (error) throw error;
      return data as {
        empresas: number;
        no_encontradas?: number;
        busquedas: number;
        max: number;
        quedan: number;
        usadas: number;
        mes: number;
        tope: number;
      };
    },
  });
  const run = async () => {
    setBusy(true);
    const { data, error } = await sb().rpc("reviews_request", {
      p_ids: ids,
      p_max: max,
      p_job: job,
    });
    setBusy(false);
    if (error) return notify(error.message, { type: "error" });
    notify(
      data
        ? `${data} en cola: empieza en menos de un minuto`
        : "Ya estaban en cola",
      { type: "success" },
    );
    qc.invalidateQueries({ queryKey: ["review_summary"] });
    qc.invalidateQueries({ queryKey: ["ai_engines"] });
    setOpen(false);
    onDone?.();
  };
  const short = est && est.busquedas > est.quedan;
  return (
    <>
      <Button size={size} variant={variant} onClick={() => setOpen(true)}>
        <Star /> {label}
      </Button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {job === "competidores"
                ? "Comparar con competidores"
                : `${label} · ${ids.length === 1 ? "esta empresa" : `${ids.length} empresas`}`}
            </DialogTitle>
            <DialogDescription>
              {job === "competidores"
                ? "Busca 3 competidores de su giro y zona y trae 28 reseñas de cada uno. Se suman al análisis porque tiene menos de 30 reseñas."
                : job === "nuevas"
                  ? "Solo trae las reseñas nuevas: se detiene en la primera que ya está guardada."
                  : "Trae las reseñas más recientes de Google (SerpApi), sin nombre ni foto del autor, y la IA las agrupa por tema (Groq primero)."}
            </DialogDescription>
          </DialogHeader>
          {askMax && job === "completa" && (
            <label className="flex items-center gap-2 text-sm">
              Reseñas por empresa (máximo 200):
              <input
                type="number"
                min={8}
                max={200}
                step={1}
                className="w-20 rounded-md border bg-background px-2 py-1"
                value={max ?? est?.max ?? 50}
                onChange={(e) =>
                  setMax(
                    Math.max(8, Math.min(200, Number(e.target.value) || 8)),
                  )
                }
              />
            </label>
          )}
          <div className="rounded-lg border p-3 text-sm">
            {isFetching || !est ? (
              "Calculando…"
            ) : (
              <>
                <p>
                  Gasta <b>{est.busquedas}</b> búsqueda
                  {est.busquedas === 1 ? "" : "s"} de SerpApi.
                </p>
                <p className="text-muted-foreground">
                  Quedan <b>{est.quedan}</b> este mes antes del tope (
                  {est.usadas} de {est.mes} usadas; se detiene al 90 %).
                </p>
                {!!est.no_encontradas && (
                  <p className="text-muted-foreground">
                    Se omiten {est.no_encontradas} marcada
                    {est.no_encontradas === 1 ? "" : "s"} como “no encontrada en
                    Google” (ábrelas y pega su enlace si la encuentras).
                  </p>
                )}
                {short && (
                  <p className="mt-1 font-semibold text-[var(--critical)]">
                    No alcanza: elige menos empresas o menos reseñas.
                  </p>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={busy}
            >
              Cancelar
            </Button>
            <Button
              onClick={run}
              disabled={busy || !est || short || isFetching}
            >
              {busy ? "Enviando…" : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/** La IA falló después de descargar: reintenta solo el análisis (0 búsquedas). */
const RetryAnalysis = ({ dirId }: { dirId: number }) => {
  const qc = useQueryClient();
  const notify = useNotify();
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={async () => {
        const { error } = await sb().rpc("reviews_retry_analysis", {
          p_dir: dirId,
        });
        if (error) return notify(error.message, { type: "error" });
        notify("En cola: no gasta búsquedas de SerpApi", { type: "success" });
        qc.invalidateQueries({ queryKey: ["review_summary"] });
      }}
    >
      Reintentar el análisis (no gasta SerpApi)
    </Button>
  );
};

/* ------------------------------------------- ficha de Google (ajustes H) */
const POR: Record<string, string> = {
  telefono: "coincide el teléfono",
  nombre: "coinciden nombre y C.P.",
  confirmada: "la confirmaste tú",
  enlace: "enlace que pegaste",
};

/** Qué ficha de Google se usó (siempre visible en la pestaña). */
const PlaceUsed = ({ s }: { s: ReviewSummary }) => {
  const { data: p } = useQuery({
    queryKey: ["review_place", s.data_id],
    enabled: !!s.data_id,
    queryFn: async () =>
      (
        await sb()
          .from("review_places")
          .select("data_id, name, address, total, rating")
          .eq("data_id", s.data_id!)
          .maybeSingle()
      ).data as Place | null,
  });
  if (!p) return null;
  const pc = s.place_check;
  return (
    <div className="flex flex-col gap-1 text-xs">
      <span className="text-muted-foreground">
        <MapPin className="inline size-3.5" /> Ficha de Google usada:{" "}
        <b className="text-foreground">{p.name}</b>
        {p.address ? ` · ${p.address}` : ""}
        {pc?.por && POR[pc.por] ? ` (${POR[pc.por]})` : ""}
      </span>
      {pc?.ok === false && (
        <span className="font-semibold text-[var(--critical)]">
          Ojo: el C.P. de esta ficha no coincide con el del directorio (se bajó
          antes de que el CRM comparara). Puede ser otra sucursal: revisa que
          sea la correcta.
        </span>
      )}
    </div>
  );
};

/** Pegar a mano el enlace de Google Maps: ahorra la búsqueda de la ficha. */
export const LinkForm = ({ dirId }: { dirId: number }) => {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const notify = useNotify();
  const qc = useQueryClient();
  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        const { error } = await sb().rpc("reviews_set_link", {
          p_dir: dirId,
          p_url: url,
        });
        setBusy(false);
        if (error) return notify(error.message, { type: "error" });
        notify("En cola: no gasta la búsqueda de la ficha", {
          type: "success",
        });
        qc.invalidateQueries({ queryKey: ["review_summary"] });
      }}
    >
      <input
        className="min-w-0 flex-1 rounded-md border bg-background px-2 py-1 text-sm"
        placeholder="Pega el enlace de Google Maps (Compartir → Copiar enlace)"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        aria-label="Enlace de Google Maps"
      />
      <Button size="sm" type="submit" disabled={busy || !url.trim()}>
        Usar este enlace
      </Button>
      <span className="w-full text-xs text-muted-foreground">
        Con el enlace no se gasta la búsqueda de la ficha: solo las páginas de
        reseñas (3 para 48 reseñas).
      </span>
    </form>
  );
};

const Yes = ({ v }: { v: boolean | null | undefined }) =>
  v == null ? (
    <span className="text-muted-foreground">sin dato</span>
  ) : v ? (
    <b className="text-[var(--good)]">coincide</b>
  ) : (
    <b className="text-[var(--critical)]">no coincide</b>
  );

/** La ficha encontrada no coincide con el directorio: Ian decide antes de bajar nada. */
const PlaceConfirm = ({ f, s }: { f: Ficha; s: ReviewSummary }) => {
  const c = s.candidate!;
  const isDueno = useIsDueno();
  const notify = useNotify();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const act = async (ok: boolean) => {
    setBusy(true);
    const { error } = await sb().rpc("reviews_confirm_place", {
      p_dir: s.directory_id,
      p_ok: ok,
    });
    setBusy(false);
    if (error) return notify(error.message, { type: "error" });
    notify(ok ? "En cola: bajando sus reseñas" : "Marcada como no encontrada", {
      type: "success",
    });
    qc.invalidateQueries({ queryKey: ["review_summary"] });
  };
  const pages = (n: number) => (n <= 8 ? 1 : 1 + Math.ceil((n - 8) / 20));
  return (
    <div className={cn(box, "flex flex-col gap-2 text-sm")}>
      <b>
        Encontré esta ficha en Google, pero no coincide con tu directorio. No
        bajé ninguna reseña: ¿es la misma empresa?
      </b>
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="rounded-md border p-2">
          <div className="text-xs text-muted-foreground">Google Maps</div>
          <b>{c.name}</b>
          <div>{c.address ?? "sin dirección"}</div>
          <div className="text-xs text-muted-foreground">
            {c.total ?? 0} calificaciones · {c.rating ?? "—"} ★
          </div>
        </div>
        <div className="rounded-md border p-2">
          <div className="text-xs text-muted-foreground">Tu directorio</div>
          <b>{f.name}</b>
          <div>{f.d?.direccion ?? "sin dirección"}</div>
          <div className="text-xs text-muted-foreground">
            Tel. {f.d?.telefono ?? "sin teléfono"}
          </div>
        </div>
      </div>
      <div className="text-xs">
        Nombre: {Math.round((c.check?.nombre ?? 0) * 100)} % de sus palabras ·
        C.P.: <Yes v={c.check?.cp} /> · Teléfono: <Yes v={c.check?.tel} /> ·
        Municipio: <Yes v={c.check?.municipio} />
      </div>
      {isDueno ? (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" disabled={busy} onClick={() => act(true)}>
            Sí, es esta (gasta{" "}
            {pages(Math.min(s.max_reviews, c.total ?? s.max_reviews))}{" "}
            búsquedas)
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => act(false)}
          >
            No es esta
          </Button>
        </div>
      ) : (
        <span className="text-xs text-muted-foreground">
          El dueño confirma si esta ficha de Google es la correcta.
        </span>
      )}
      <span className="text-xs text-muted-foreground">
        ¿Tienes la correcta? Pega su enlace:
      </span>
      <LinkForm dirId={s.directory_id} />
    </div>
  );
};

/** No se encontró en Google: no se vuelve a buscar sola. */
const NotFound = ({ s }: { s: ReviewSummary }) => {
  const notify = useNotify();
  const qc = useQueryClient();
  return (
    <div className={cn(box, "flex flex-col gap-2 text-sm")}>
      <b>No encontrada en Google · {fecha(s.not_found_at)}</b>
      <span className="text-muted-foreground">
        La busqué por nombre, municipio y teléfono y ninguna ficha se parecía.
        El CRM no la vuelve a buscar solo (ni en la selección masiva del
        Directorio). Si la encuentras en Google Maps, pega su enlace:
      </span>
      <LinkForm dirId={s.directory_id} />
      <div>
        <Button
          size="sm"
          variant="outline"
          onClick={async () => {
            const { error } = await sb().rpc("reviews_search_again", {
              p_dir: s.directory_id,
            });
            if (error) return notify(error.message, { type: "error" });
            notify("En cola", { type: "success" });
            qc.invalidateQueries({ queryKey: ["review_summary"] });
          }}
        >
          Buscar otra vez (1 búsqueda + reseñas)
        </Button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------ pestaña */
/** Cambio en puntos de % de reseñas que se quejan del tema: últimos 90 días vs. los 90 anteriores. */
const Trend = ({ tr }: { tr: number | null }) =>
  tr == null ? (
    <span
      className="text-muted-foreground"
      title="Faltan reseñas de hace 3 a 6 meses para comparar"
    >
      sin datos para comparar
    </span>
  ) : tr >= 5 ? (
    <span
      className="font-bold text-[var(--critical)]"
      title="Puntos de % frente a los 90 días anteriores"
    >
      ▲ +{tr} pts
    </span>
  ) : tr <= -5 ? (
    <span
      className="font-bold text-[var(--good)]"
      title="Puntos de % frente a los 90 días anteriores"
    >
      ▼ {tr} pts
    </span>
  ) : (
    <span className="text-muted-foreground">estable</span>
  );
const Stars = ({ n }: { n: number }) => (
  <b
    style={{
      color: n <= 2 ? "var(--critical)" : n >= 4 ? "var(--good)" : undefined,
    }}
  >
    {"★".repeat(n)}
    {"☆".repeat(5 - n)}
  </b>
);
const Box = ({
  title,
  color,
  children,
}: {
  title: string;
  color?: string;
  children: React.ReactNode;
}) => (
  <div
    className={cn(box, "flex flex-col gap-2")}
    style={color ? { borderColor: color } : undefined}
  >
    <h4 className="font-bold">{title}</h4>
    {children}
  </div>
);

const STATUS: Record<string, string> = {
  en_cola: "En cola: empieza en menos de un minuto.",
  descargando: "Descargando reseñas de Google…",
  analizando: "La IA está agrupando las reseñas por tema…",
};

export const ReviewsTab = ({ f }: { f: Ficha }) => {
  const dirId = f.d?.id;
  const { data: s } = useReviewSummary(dirId);
  const { data: T } = useReviewThemes();
  const { data: offers } = useOffers();
  const { data: brief } = useReviewBrief(dirId);
  const [filter, setFilter] = useState<"todas" | "neg" | "pos">("todas");
  const [theme, setTheme] = useState("");
  const { data: rows } = useQuery({
    queryKey: ["reviews", dirId, s?.fetched_at],
    enabled: !!dirId && !!s?.fetched_at,
    queryFn: async () => {
      const { data } = await sb()
        .from("reviews")
        .select(
          "id, rating, body, published_at, owner_replied, owner_reply, themes, neg_themes, other_theme",
        )
        .eq("directory_id", dirId!)
        .order("published_at", { ascending: false })
        .limit(300);
      return (data ?? []) as ReviewRow[];
    },
  });
  if (!dirId)
    return (
      <div className={box}>
        Esta empresa no tiene fila en el directorio todavía: investígala con IA
        o captura su nombre en Datos para poder buscar sus reseñas.
      </div>
    );
  const offerName = (slug: string) =>
    offers?.find((o) => o.slug === slug)?.name ?? slug;
  const lab = (k: string) => T?.get(k)?.label ?? k;
  const busy = s && BUSY.includes(s.status);
  const cold = (f.d?.thermo_base ?? 0) < 40;

  if (s?.candidate && !busy) return <PlaceConfirm f={f} s={s} />;
  if (s?.not_found_at && !s.data_id && !busy) return <NotFound s={s} />;
  if (!s?.fetched_at || !s.themes)
    return (
      <div className={cn(box, "flex flex-col gap-2")}>
        <b>
          {busy
            ? STATUS[s!.status]
            : s?.status === "error"
              ? `No se pudo: ${s.error}`
              : s?.status === "vinculada"
                ? "Ficha confirmada: falta bajar sus reseñas."
                : "Todavía no analizamos sus reseñas."}
        </b>
        <span className="text-sm text-muted-foreground">
          El CRM descarga sus reseñas de Google (las más recientes, 48 por
          omisión y hasta 200), sin nombre ni foto del autor, y la IA las agrupa
          por tema: qué aman sus clientes, de qué se quejan, qué quejas van en
          aumento y cuál de tus productos resuelve cada una. Antes de gastar te
          dice cuántas búsquedas de SerpApi usa y cuántas quedan en el mes.
        </span>
        {cold && !busy && (
          <span className="text-sm text-muted-foreground">
            Su termómetro es frío: conviene gastar el cupo primero en empresas
            tibias o más calientes.
          </span>
        )}
        {!busy && (
          <div>
            {s?.status === "error" && s.job === "analisis" ? (
              <RetryAnalysis dirId={dirId} />
            ) : (
              <ReviewsButton ids={[dirId]} />
            )}
          </div>
        )}
      </div>
    );

  const th = s.themes ?? [];
  const dist = s.dist ?? [0, 0, 0, 0, 0];
  const n = dist.reduce((a, b) => a + b, 0);
  const mx = Math.max(1, ...dist);
  const pains = th
    .filter((t) => t.pain && t.k !== "otro")
    .sort(
      (a, b) =>
        b.m * b.neg * (1 + Math.max(0, b.tr ?? 0) / 20) -
        a.m * a.neg * (1 + Math.max(0, a.tr ?? 0) / 20),
    );
  const solved = pains.filter((p) => T?.get(p.k)?.offer_slugs.length);
  const other = pains.filter((p) => !T?.get(p.k)?.offer_slugs.length);
  const others = [
    ...new Set((rows ?? []).map((r) => r.other_theme).filter(Boolean)),
  ] as string[];
  const loves = th.filter(
    (t) => t.neg < 25 && t.m >= 2 && !["personal", "otro"].includes(t.k),
  );
  const quote = (k: string) =>
    rows?.find((r) => r.neg_themes.includes(k) && r.body)?.body;
  const opener = revOpener(brief, offerName);
  const wa =
    f.dnc || !opener ? null : waHref(f.phone, withOptOut("whatsapp", opener));
  const list = (rows ?? []).filter(
    (r) =>
      (filter === "todas" ||
        (filter === "neg" ? r.rating <= 2 : r.rating >= 4)) &&
      (!theme || r.themes.includes(theme)),
  );
  const fewOwn = (s.total ?? 0) < 30;
  const withText = (rows ?? []).filter((r) => r.body).length;

  return (
    <div className="flex flex-col gap-3">
      <PlaceUsed s={s} />
      {busy && <div className="text-sm font-semibold">{STATUS[s.status]}</div>}
      {s.status === "error" && (
        <div className="text-sm font-semibold text-[var(--critical)]">
          Último intento: {s.error}{" "}
          {s.job === "analisis" && <RetryAnalysis dirId={dirId} />}
        </div>
      )}
      {s.includes_competitors && (
        <div className={cn(box, "border-[var(--warning,#d4a017)] text-sm")}>
          <b>Tiene solo {s.total} reseñas: muestra chica.</b> Para no sacar
          conclusiones con pocos datos, sumamos las reseñas de{" "}
          {s.competitors?.length ?? 0} competidores de su giro y zona. Son los
          dolores del mercado; muy probablemente también los suyos.
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-3">
        <div className={box}>
          <div className="text-xs text-muted-foreground">
            Calificación en Google
          </div>
          <div className="text-2xl font-extrabold">{s.rating ?? "—"} ★</div>
          <div
            className="text-xs text-muted-foreground"
            title="Google cuenta todas las calificaciones, pero no deja ver algunas (las que filtra o de cuentas borradas); muchas son solo estrellas, sin texto."
          >
            {(s.total ?? 0).toLocaleString("es-MX")} calificaciones
            {s.own_total != null && s.own_total < (s.total ?? 0)
              ? ` · ${s.own_total.toLocaleString("es-MX")} bajadas`
              : ""}{" "}
            · {withText} con texto
          </div>
        </div>
        <div className={box}>
          <div className="text-xs text-muted-foreground">Negativas (1–2 ★)</div>
          <div className="text-2xl font-extrabold text-[var(--critical)]">
            {pct(dist[0] + dist[1], n)}%
          </div>
          <div className="text-xs text-muted-foreground">
            Positivas (4–5 ★): {pct(dist[3] + dist[4], n)}%
          </div>
        </div>
        <div className={box}>
          <div className="text-xs text-muted-foreground">El dueño responde</div>
          <div className="text-2xl font-extrabold">
            {s.owner_reply_rate ?? 0}%
          </div>
          <div className="text-xs text-muted-foreground">
            {(s.owner_reply_rate ?? 0) < 30
              ? "Casi no responde: su reputación está desatendida"
              : "Cuida su reputación"}
          </div>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Box title="Distribución">
          {[5, 4, 3, 2, 1].map((st) => (
            <div
              key={st}
              className="grid grid-cols-[34px_1fr_36px] items-center gap-2 text-sm"
            >
              <span>{st} ★</span>
              <span className="h-2 rounded-full bg-secondary">
                <i
                  className="block h-2 rounded-full"
                  style={{
                    width: `${(dist[st - 1] / mx) * 100}%`,
                    background:
                      st <= 2
                        ? "var(--critical)"
                        : st === 3
                          ? "var(--th-2)"
                          : "var(--good)",
                  }}
                />
              </span>
              <b className="text-right">{dist[st - 1]}</b>
            </div>
          ))}
        </Box>
        <Box title="Calificación por trimestre (sus reseñas)">
          {s.by_quarter?.length ? (
            <div className="flex h-24 items-end gap-2">
              {s.by_quarter.map((q) => (
                <div
                  key={q.q}
                  className="flex flex-1 flex-col items-center gap-1 text-xs"
                  title={`${q.q}: ${q.rating} ★ (${q.n})`}
                >
                  <span>{q.rating}</span>
                  <span
                    className="w-full max-w-8 rounded-t bg-[var(--seq-4,var(--primary))]"
                    style={{
                      height: `${Math.max(4, ((q.rating - 1) / 4) * 60)}px`,
                    }}
                  />
                  <span className="text-muted-foreground">
                    {q.q.replace(/^\d{2}/, "")}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">
              Sin fechas suficientes.
            </span>
          )}
        </Box>
      </div>

      <Box
        title="Dolores que tus productos resuelven · de mayor a menor"
        color="var(--critical)"
      >
        {solved.length ? (
          solved.map((p, i) => {
            const t = T?.get(p.k);
            const q = quote(p.k);
            return (
              <div
                key={p.k}
                className={cn("grid gap-1 py-1.5", i && "border-t")}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <b>
                    {i + 1}. {lab(p.k)}
                  </b>
                  <span className="rounded-full border px-2 py-0.5 text-xs">
                    {p.m} reseñas · {p.neg}% negativas
                  </span>
                  <span className="text-xs">
                    <Trend tr={p.tr} />
                  </span>
                </div>
                {q && <p className="text-sm italic">“{q.slice(0, 220)}”</p>}
                {t?.offer_slugs.map((o) => (
                  <div
                    key={o}
                    className="flex flex-wrap items-start gap-2 text-sm"
                  >
                    <span className="inline-flex items-center gap-1 font-semibold">
                      <OfferDot
                        color={offerColor(offers?.find((x) => x.slug === o))}
                      />
                      {offerName(o)}
                    </span>
                    <span className="min-w-48 flex-1">
                      {t.solutions[o] ?? ""}
                      {!f.d?.fits?.includes(o) && (
                        <b className="text-[var(--ai)]">
                          {" "}
                          Oportunidad nueva: no estaba en su segmento.
                        </b>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            );
          })
        ) : (
          <p className="text-sm text-muted-foreground">
            Ninguna queja relevante coincide con tus productos.
          </p>
        )}
        {(other.length > 0 || others.length > 0) && (
          <div className="border-t pt-2 text-sm">
            <b>Otras quejas</b>{" "}
            <span className="text-muted-foreground">
              (ningún producto tuyo las resuelve; úsalas solo como contexto):
            </span>{" "}
            {other
              .map((p) => `${lab(p.k)} (${p.m} · ${p.neg}% neg.)`)
              .join(" · ")}
            {others.length > 0 && (
              <span className="text-muted-foreground">
                {other.length ? " · " : ""}Fuera del catálogo:{" "}
                {others.slice(0, 6).join(", ")}
              </span>
            )}
          </div>
        )}
      </Box>

      <Box title="Lo que sus clientes aman" color="var(--good)">
        {loves.length ? (
          <ul className="list-disc pl-5 text-sm">
            {loves.map((g) => (
              <li key={g.k}>
                <b>{T?.get(g.k)?.praise ?? lab(g.k)}</b>{" "}
                <span className="text-muted-foreground">
                  · {g.m} menciones, {100 - g.neg}% positivas
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">Nada destaca.</p>
        )}
        <span className="text-xs text-muted-foreground">
          Empieza la conversación por aquí: reconocer lo que hacen bien abre la
          puerta a hablar de lo que falla.
        </span>
      </Box>

      {opener && (
        <Box title="Propuesta basada en sus reseñas" color="var(--ai)">
          <Quote>{opener}</Quote>
          <ul className="list-disc pl-5 text-sm">
            <li>
              Menciona el patrón («varias reseñas dicen…»), nunca a un cliente
              en particular.
            </li>
            <li>
              Nunca digas «leí sus reseñas malas»: habla de lo que comentan sus
              clientes.
            </li>
            <li>
              Pregunta antes de proponer: «¿les pasa seguido?». Que él confirme
              el dolor.
            </li>
            <li>
              Cierra con algo medible: «¿cuántas ventas creen que se van por
              esto al mes?»
            </li>
          </ul>
          <div className="flex flex-wrap gap-1.5">
            <Button asChild={!!wa} size="sm" variant="outline" disabled={!wa}>
              {wa ? (
                <a href={wa} target="_blank" rel="noreferrer">
                  <MessageCircle /> Usar en WhatsApp
                </a>
              ) : (
                <span>
                  <MessageCircle /> Usar en WhatsApp
                </span>
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigator.clipboard?.writeText(opener)}
            >
              <Copy /> Copiar guion
            </Button>
          </div>
        </Box>
      )}

      <Box title="Todos los temas">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-1.5 pr-3">Tema</th>
                <th className="py-1.5 pr-3">Menciones</th>
                <th className="py-1.5 pr-3">% negativo</th>
                <th className="py-1.5 pr-3">Últimos 90 días</th>
                <th className="py-1.5">Lo resuelve</th>
              </tr>
            </thead>
            <tbody>
              {th.map((t) => (
                <tr key={t.k} className="border-b last:border-0">
                  <td className="py-1.5 pr-3">{lab(t.k)}</td>
                  <td className="py-1.5 pr-3">{t.m}</td>
                  <td className="py-1.5 pr-3">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-1.5 w-20 rounded-full bg-secondary">
                        <i
                          className="block h-1.5 rounded-full"
                          style={{
                            width: `${t.neg}%`,
                            background:
                              t.neg >= 50
                                ? "var(--critical)"
                                : t.neg >= 25
                                  ? "var(--th-3)"
                                  : "var(--good)",
                          }}
                        />
                      </span>
                      <b className="text-xs">{t.neg}%</b>
                    </span>
                  </td>
                  <td className="py-1.5 pr-3 text-xs">
                    <Trend tr={t.tr} />
                  </td>
                  <td className="py-1.5">
                    {T?.get(t.k)?.offer_slugs.map(offerName).join(", ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Box>

      <Box title="Contra sus competidores">
        {s.competitors?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-1.5 pr-3">Empresa</th>
                  <th className="py-1.5 pr-3">★</th>
                  <th className="py-1.5 pr-3">Reseñas</th>
                  <th className="py-1.5">Su queja principal</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-1.5 pr-3 font-bold">{f.name}</td>
                  <td className="py-1.5 pr-3 font-bold">{s.rating}</td>
                  <td className="py-1.5 pr-3">{s.total}</td>
                  <td className="py-1.5">{pains[0] ? lab(pains[0].k) : "—"}</td>
                </tr>
                {s.competitors.map((c) => (
                  <tr key={c.name} className="border-b last:border-0">
                    <td className="py-1.5 pr-3">{c.name}</td>
                    <td className="py-1.5 pr-3">{c.rating}</td>
                    <td className="py-1.5 pr-3">{c.total}</td>
                    <td className="py-1.5">{c.pain ? lab(c.pain) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {s.competitors.some((c) => c.rating > (s.rating ?? 0)) && (
              <span className="text-xs text-muted-foreground">
                Hay competidores mejor calificados: argumento de urgencia («sus
                clientes tienen alternativas cerca»).
              </span>
            )}
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">
            {fewOwn
              ? "Tiene menos de 30 reseñas: compara con 3 competidores de su giro y zona para no sacar conclusiones con pocos datos."
              : "Tiene reseñas suficientes; la comparación con competidores es opcional."}
          </span>
        )}
        {!busy && fewOwn && !s.competitors?.length && (
          <div>
            <ReviewsButton
              ids={[dirId]}
              job="competidores"
              label="Comparar con competidores"
              variant="outline"
              askMax={false}
            />
          </div>
        )}
      </Box>

      <Box title="Reseñas">
        <div className="flex flex-wrap items-center gap-2">
          <div
            className="inline-flex rounded-lg border p-0.5"
            role="group"
            aria-label="Filtrar reseñas"
          >
            {(
              [
                ["todas", "Todas"],
                ["neg", "Negativas"],
                ["pos", "Positivas"],
              ] as const
            ).map(([k, l]) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                className={cn(
                  "rounded-md px-2.5 py-1 text-xs font-semibold",
                  filter === k && "bg-secondary",
                )}
              >
                {l}
              </button>
            ))}
          </div>
          <select
            aria-label="Filtrar por tema"
            className="rounded-md border bg-background px-2 py-1 text-xs"
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
          >
            <option value="">Todos los temas</option>
            {th.map((t) => (
              <option key={t.k} value={t.k}>
                {lab(t.k)}
              </option>
            ))}
          </select>
          <span className="text-xs text-muted-foreground">
            {list.length} reseñas
          </span>
        </div>
        <div className="flex max-h-[32rem] flex-col gap-2 overflow-y-auto">
          {list.map((r) => (
            <div
              key={r.id}
              className="grid gap-1 rounded-lg border p-2.5 text-sm"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Stars n={r.rating} />
                {r.themes.map((k) => (
                  <span
                    key={k}
                    className={cn(
                      "rounded-full border px-2 py-0.5 text-xs",
                      r.neg_themes.includes(k) && "border-[var(--critical)]",
                    )}
                  >
                    {k === "otro" && r.other_theme
                      ? `Otro: ${r.other_theme}`
                      : lab(k)}
                  </span>
                ))}
                <span className="text-xs text-muted-foreground">
                  {fecha(r.published_at)}
                </span>
              </div>
              <span>
                {r.body ?? (
                  <i className="text-muted-foreground">
                    Solo calificación, sin texto.
                  </i>
                )}
              </span>
              {r.owner_replied && (
                <span className="border-l-2 pl-2 text-xs text-muted-foreground">
                  <b>Respuesta del dueño:</b> {r.owner_reply ?? "(sin texto)"}
                </span>
              )}
            </div>
          ))}
        </div>
      </Box>

      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>
          Reseñas: SerpApi · descargadas el {fecha(s.fetched_at)} · {s.searches}{" "}
          búsqueda{s.searches === 1 ? "" : "s"} gastadas en total · se revisan
          solo las nuevas cada 60 días
        </span>
        {!busy && (
          <>
            <ReviewsButton
              ids={[dirId]}
              job="nuevas"
              label="Revisar nuevas"
              variant="outline"
              askMax={false}
            />
            {(s.own_total ?? 0) < Math.min(200, s.total ?? 0) && (
              <ReviewsButton
                ids={[dirId]}
                label="Traer más (hasta 200)"
                variant="outline"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------ IA y ajustes */
const PROVIDERS: [string, string, string][] = [
  [
    "serpapi",
    "SerpApi (activo)",
    "Trae las reseñas más recientes: la 1.ª página 8 y las siguientes 20 por búsqueda. Plan gratis: 250 búsquedas al mes.",
  ],
  [
    "outscraper",
    "Outscraper (opción, de pago)",
    "Trae todas de una vez y cobra por reseña. No está activo: necesita tarjeta y tu permiso.",
  ],
  [
    "places",
    "Google Places oficial (opción)",
    "Solo da 5 reseñas por negocio y pide tarjeta en Google Cloud. No está activo.",
  ],
];

export const ReviewsSettings = () => {
  const notify = useNotify();
  const qc = useQueryClient();
  const { data: cfg } = useQuery({
    queryKey: ["crm_settings", "reviews"],
    queryFn: async () => {
      const { data } = await sb()
        .from("crm_settings")
        .select("reviews_max, reviews_refresh_days")
        .eq("id", 1)
        .single();
      return data as { reviews_max: number; reviews_refresh_days: number };
    },
  });
  const { data: left } = useQuery({
    queryKey: ["serpapi_left"],
    queryFn: async () =>
      (await sb().rpc("serpapi_left")).data as {
        usadas: number;
        mes: number;
        tope: number;
        quedan: number;
      },
  });
  const save = async (patch: Record<string, number>) => {
    const { data, error } = await sb()
      .from("crm_settings")
      .update(patch)
      .eq("id", 1)
      .select("id");
    if (error || !data?.length)
      return notify("Solo un administrador puede cambiar esto", {
        type: "error",
      });
    qc.invalidateQueries({ queryKey: ["crm_settings"] });
    notify("Guardado", { type: "success" });
  };
  if (!cfg) return null;
  const perCo = (n: number) => 1 + (n <= 8 ? 1 : 1 + Math.ceil((n - 8) / 20));
  return (
    <div className="flex flex-col gap-3 text-sm">
      {left && (
        <p>
          SerpApi este mes: <b>{left.usadas}</b> de {left.mes} búsquedas ·
          quedan <b>{left.quedan}</b> antes del tope del 90 % ({left.tope}).
        </p>
      )}
      <fieldset className="grid gap-1.5">
        <legend className="mb-1 font-semibold">Proveedor</legend>
        {PROVIDERS.map(([k, l, d]) => (
          <label
            key={k}
            className={cn(
              "flex items-start gap-2",
              k !== "serpapi" && "opacity-60",
            )}
          >
            <input
              type="radio"
              name="revprov"
              checked={k === "serpapi"}
              disabled={k !== "serpapi"}
              readOnly
              className="mt-1"
            />
            <span>
              <b>{l}</b>
              <span className="block text-xs text-muted-foreground">{d}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <label className="flex flex-wrap items-center gap-2">
        Reseñas por empresa (valor inicial)
        <input
          type="number"
          min={8}
          max={200}
          defaultValue={cfg.reviews_max}
          className="w-20 rounded-md border bg-background px-2 py-1"
          onBlur={(e) => {
            const n = Math.max(8, Math.min(200, Number(e.target.value) || 50));
            if (n !== cfg.reviews_max) save({ reviews_max: n });
          }}
        />
        <span className="text-xs text-muted-foreground">
          = {perCo(cfg.reviews_max)} búsquedas por empresa nueva (≈{" "}
          {Math.floor((left?.tope ?? 225) / perCo(cfg.reviews_max))} empresas al
          mes). 48 gasta una búsqueda menos que 50.
        </span>
      </label>
      <label className="flex flex-wrap items-center gap-2">
        Revisar solo reseñas nuevas cada
        <input
          type="number"
          min={0}
          max={365}
          defaultValue={cfg.reviews_refresh_days}
          className="w-20 rounded-md border bg-background px-2 py-1"
          onBlur={(e) => {
            const n = Math.max(0, Math.min(365, Number(e.target.value) || 0));
            if (n !== cfg.reviews_refresh_days)
              save({ reviews_refresh_days: n });
          }}
        />
        días{" "}
        <span className="text-xs text-muted-foreground">
          (1 búsqueda por empresa ya analizada; 0 = apagado)
        </span>
      </label>
      <p className="text-xs text-muted-foreground">
        Las reseñas solo se piden cuando tú lo decides (ficha o selección del
        Directorio); la investigación nocturna no las pide. Nunca se guarda el
        nombre ni la foto de quien escribió la reseña.
      </p>
    </div>
  );
};
